import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { 
  uploadPartnerDocument, getPartnerPatientDocuments, 
  downloadPartnerDocument, deletePartnerDocument, getDocumentCategories 
} from "@/lib/api";
import { 
  Users, User, Activity, Clock, AlertCircle, CheckCircle2,
  Building2, Hospital, Stethoscope, Search, ChevronRight,
  Pill, Heart, MessageSquare, FileText, Phone, Mail,
  Calendar, TrendingUp, Lock, Loader2, Upload, Download,
  FolderOpen, File, Trash2, Eye, X, RefreshCw
} from "lucide-react";

export default function PartnerDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientActivity, setPatientActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("activity");
  
  // Document upload state
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    description: "",
    category: "general"
  });
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);

  const isAdmin = user?.role && ["admin", "administrator"].includes(user.role.toLowerCase());
  const isPartner = user?.role && ["facility", "hospital", "physician"].includes(user.role);
  const hasAccess = isAdmin || isPartner;
  const isApproved = isAdmin || user?.approved !== false;

  useEffect(() => {
    if (!authLoading && hasAccess && isApproved) {
      loadPatients();
      loadCategories();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const loadPatients = async () => {
    setLoading(true);
    try {
      const res = await api.get("/partners/my-patients");
      setPatients(res.data.patients || []);
    } catch (error) {
      console.error("Error loading patients:", error);
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await getDocumentCategories();
      setCategories(data.categories || []);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const viewPatientActivity = async (patientId) => {
    setActivityLoading(true);
    try {
      const res = await api.get(`/partners/patient/${patientId}/activity`);
      setPatientActivity(res.data);
      setSelectedPatient(patients.find(p => p.id === patientId));
      // Also load documents for this patient
      loadPatientDocuments(patientId);
    } catch (error) {
      console.error("Error loading activity:", error);
      toast.error("Failed to load patient activity");
    } finally {
      setActivityLoading(false);
    }
  };

  const loadPatientDocuments = async (patientId) => {
    setDocumentsLoading(true);
    try {
      const data = await getPartnerPatientDocuments(patientId);
      setDocuments(data.documents || []);
    } catch (error) {
      console.error("Error loading documents:", error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadData.file || !selectedPatient) {
      toast.error("Please select a file");
      return;
    }
    
    setUploading(true);
    try {
      await uploadPartnerDocument(
        selectedPatient.id,
        uploadData.file,
        uploadData.description || null,
        uploadData.category
      );
      toast.success("Document uploaded successfully");
      setShowUploadModal(false);
      setUploadData({ file: null, description: "", category: "general" });
      loadPatientDocuments(selectedPatient.id);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const blob = await downloadPartnerDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Delete this document?")) return;
    
    try {
      await deletePartnerDocument(documentId);
      toast.success("Document deleted");
      loadPatientDocuments(selectedPatient.id);
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType) => {
    if (['pdf'].includes(fileType)) return <FileText className="w-5 h-5 text-red-500" />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileType)) return <Eye className="w-5 h-5 text-blue-500" />;
    if (['doc', 'docx'].includes(fileType)) return <File className="w-5 h-5 text-blue-600" />;
    if (['xls', 'xlsx'].includes(fileType)) return <File className="w-5 h-5 text-green-600" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const getPartnerIcon = () => {
    switch (user?.role) {
      case "facility": return Building2;
      case "hospital": return Hospital;
      case "physician": return Stethoscope;
      default: return Building2;
    }
  };

  const getPartnerTitle = () => {
    switch (user?.role) {
      case "facility": return "Facility";
      case "hospital": return "Hospital";
      case "physician": return "Physician";
      default: return "Partner";
    }
  };

  const PartnerIcon = getPartnerIcon();

  const filteredPatients = patients.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">Partner Access Required</h2>
            <p className="text-muted-foreground mb-6">
              This dashboard is for registered healthcare partners.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/")}>
                Return Home
              </Button>
              <Link to="/partner-register">
                <Button>Register as Partner</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isApproved) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">Account Pending Approval</h2>
            <p className="text-muted-foreground mb-6">
              Your {getPartnerTitle().toLowerCase()} account is currently being reviewed by our team.
              You'll be notified once approved.
            </p>
            <p className="text-sm text-muted-foreground">
              Questions? Call <a href="tel:703-578-7108" className="text-primary">703-578-7108</a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="partner-dashboard">
      <div className="container-max px-6 md:px-12">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <PartnerIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-serif text-2xl text-foreground">
                  {user?.organization_name || `${getPartnerTitle()} Portal`}
                </h1>
                <p className="text-muted-foreground text-sm">
                  {isAdmin ? "Admin View - All Patients" : `${getPartnerTitle()} Dashboard`}
                </p>
              </div>
            </div>
          </div>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{patients.length}</p>
                  <p className="text-xs text-muted-foreground">Assigned Patients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Activity className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {patients.filter(p => p.recent_checkins?.length > 0).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Active This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {patients.filter(p => {
                      const lastCheckin = p.recent_checkins?.[0];
                      if (!lastCheckin) return true;
                      const daysSince = (new Date() - new Date(lastCheckin.timestamp)) / (1000 * 60 * 60 * 24);
                      return daysSince > 3;
                    }).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Need Follow-up</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FolderOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{documents.length}</p>
                  <p className="text-xs text-muted-foreground">Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Patient List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Your Assigned Patients
                </CardTitle>
                <CardDescription>
                  View and manage patients assigned to your organization
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button variant="outline" size="icon" onClick={loadPatients}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredPatients.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No patients assigned</p>
                <p className="text-sm">
                  Patients will appear here once assigned by Goodwin Cares.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPatients.map((patient) => {
                  const lastCheckin = patient.recent_checkins?.[0];
                  const daysSinceCheckin = lastCheckin 
                    ? Math.floor((new Date() - new Date(lastCheckin.timestamp)) / (1000 * 60 * 60 * 24))
                    : null;
                  const needsAttention = daysSinceCheckin === null || daysSinceCheckin > 3;
                  
                  return (
                    <div 
                      key={patient.id}
                      className={`flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer ${
                        needsAttention ? 'border-amber-200 bg-amber-50/50' : ''
                      }`}
                      onClick={() => viewPatientActivity(patient.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${needsAttention ? 'bg-amber-100' : 'bg-primary/10'}`}>
                          <User className={`w-5 h-5 ${needsAttention ? 'text-amber-600' : 'text-primary'}`} />
                        </div>
                        <div>
                          <p className="font-medium">{patient.name}</p>
                          <p className="text-sm text-muted-foreground">{patient.email}</p>
                          {patient.disease_category && (
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {patient.disease_category.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            {lastCheckin ? (
                              <span className={needsAttention ? 'text-amber-600' : 'text-muted-foreground'}>
                                {daysSinceCheckin === 0 ? 'Today' : 
                                 daysSinceCheckin === 1 ? 'Yesterday' : 
                                 `${daysSinceCheckin} days ago`}
                              </span>
                            ) : (
                              <span className="text-amber-600">No check-ins</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">Last activity</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="mt-6 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <span className="text-muted-foreground">Goodwin Hospice Support:</span>
              <a href="tel:703-578-7108" className="flex items-center gap-1 text-primary hover:underline">
                <Phone className="w-4 h-4" />
                703-578-7108
              </a>
              <a href="mailto:hospice@goodwinliving.org" className="flex items-center gap-1 text-primary hover:underline">
                <Mail className="w-4 h-4" />
                hospice@goodwinliving.org
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Patient Activity Dialog */}
      <Dialog open={!!selectedPatient} onOpenChange={() => { setSelectedPatient(null); setPatientActivity(null); setDocuments([]); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {selectedPatient?.name || "Patient"} - Records
            </DialogTitle>
            <DialogDescription>
              View patient activity and manage documents
            </DialogDescription>
          </DialogHeader>
          
          {activityLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : patientActivity ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="documents">
                  Documents
                  {documents.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{documents.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="activity" className="space-y-6">
                {/* Patient Info */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <p className="font-medium">{patientActivity.patient?.email}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Condition:</span>
                      <p className="font-medium capitalize">
                        {patientActivity.patient?.disease_category?.replace(/_/g, ' ') || 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recent Check-ins */}
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Recent Check-ins ({patientActivity.checkins?.length || 0})
                  </h3>
                  {patientActivity.checkins?.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {patientActivity.checkins.slice(0, 10).map((checkin, idx) => (
                        <div key={idx} className="p-3 bg-muted/30 rounded-lg border">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium">
                                Pain: {checkin.pain_level}/10 | Mood: {checkin.mood || 'N/A'}
                              </p>
                              {checkin.symptoms?.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Symptoms: {checkin.symptoms.join(', ')}
                                </p>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(checkin.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No check-ins recorded</p>
                  )}
                </div>

                {/* Medications */}
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Pill className="w-4 h-4" />
                    Medications ({patientActivity.medications?.length || 0})
                  </h3>
                  {patientActivity.medications?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {patientActivity.medications.map((med, idx) => (
                        <div key={idx} className="p-2 bg-muted/30 rounded border text-sm">
                          <p className="font-medium">{med.name}</p>
                          <p className="text-xs text-muted-foreground">{med.dosage} - {med.frequency}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No medications listed</p>
                  )}
                </div>

                {/* Journal Summary */}
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Recent Journal Entries ({patientActivity.journals?.length || 0})
                  </h3>
                  {patientActivity.journals?.length > 0 ? (
                    <div className="space-y-1">
                      {patientActivity.journals.map((journal, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                          <span className="text-sm">Entry - Mood: {journal.mood || 'N/A'}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(journal.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No journal entries</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="documents" className="space-y-4">
                {/* Upload Button */}
                <div className="flex justify-between items-center">
                  <h3 className="font-medium flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Patient Documents
                  </h3>
                  <Button size="sm" onClick={() => setShowUploadModal(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </div>

                {/* Documents List */}
                {documentsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p className="text-sm">No documents uploaded yet</p>
                    <p className="text-xs">Upload patient records for viewing</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.file_type)}
                          <div>
                            <p className="font-medium text-sm">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(doc.file_size)} • {doc.category} • {new Date(doc.uploaded_at).toLocaleDateString()}
                            </p>
                            {doc.description && (
                              <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleDownloadDocument(doc)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteDocument(doc.id)}>
                            <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Upload Document Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Patient Document</DialogTitle>
            <DialogDescription>
              Upload medical records, lab results, or other documents for {selectedPatient?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Select File</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xlsx,.xls"
                onChange={(e) => setUploadData({...uploadData, file: e.target.files?.[0]})}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Max 10MB. Allowed: PDF, Word, Excel, Images, Text
              </p>
            </div>
            
            <div>
              <Label>Category</Label>
              <Select value={uploadData.category} onValueChange={(v) => setUploadData({...uploadData, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                placeholder="Brief description of the document..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={!uploadData.file || uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
